
// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { ActionTree, MutationTree } from 'vuex';
import { logErrorToConsole } from '@/utils/apiUtils';
import { IState } from './index';

const namespaced = (val: string) => `user/${val}`;

export const actionTypes = Object.freeze({
    CLAIM_ACCOUNT: namespaced('claimAccount'),
    IS_ACCOUNT_CLAIMED: namespaced('isAccountClaimed'),
    VERIFY_EMAIL: namespaced('verifyEmail'),
});

interface User {
    firstName: string,
    email: string,
}

export class State {
    user: User | null;

    constructor() {
        this.user = null;
    }
}

export const mutations: MutationTree<State> = {
    updateUser(state, user) {
        state.user = user;
    },
};

export const actions: ActionTree<State, IState> = {
    async claimAccount(_context, params) {
        try {
            const apiResponse = await fetch('/api/user/claim', {
                credentials: 'same-origin',
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: params.userId,
                    password: params.password,
                    token: params.token,
                }),
            });

            if (apiResponse.ok) {
                return {
                    successResponse: true,
                    destination: params.destination,
                };
            }
        } catch (error) {
            logErrorToConsole(error);
            return { successResponse: false };
        }
    },
    isAccountClaimed(context, params) {
        return fetch(
            `/api/account/claim/status?userId=${params.userId
            }&token=${encodeURIComponent(params.token)}&returnUrl=${params.url
            }`,
            {
                credentials: 'same-origin',
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            }
        )
        .then((r) =>
            r.json().then((data) => ({ status: r.status, body: data }))
        )
        .then((response) => {
            context.commit('updateUser', response.body);
            return response;
        });
    },
    verifyEmail(_context, params) {
        return fetch('/api/v2/customer/email/confirm', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: params.userId,
                token: params.token,
            }),
        });
    },
};


export default {
    namespaced: true,
    state: new State(),
    actions,
    mutations,
};
